import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  type JurisdictionDocumentType,
  createTemplateDocumentEndpoint,
  deleteTemplateDocumentEndpoint,
  getAllTemplateDocumentsStatus,
} from "@/hera-api/client";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface LicenseTypeTemplatesSectionProps {
  licenseTypeId: string | null;
}

export function LicenseTypeTemplatesSection({
  licenseTypeId,
}: LicenseTypeTemplatesSectionProps) {
  const [deletedTemplateDialog, setDeletedTemplateDialog] = useState<{
    isOpen: boolean;
    documentType: JurisdictionDocumentType | null;
  }>({
    isOpen: false,
    documentType: null,
  });

  const { isAuthSetupComplete } = useAuthQuerySetup();
  const queryClient = useQueryClient();

  // Fetch template documents status
  const {
    data: templatesData,
    isLoading: templatesLoading,
    refetch: refetchTemplates,
  } = useQuery({
    queryKey: ["template-documents-status", licenseTypeId],
    queryFn: async () => {
      if (!licenseTypeId) return null;
      const response = await getAllTemplateDocumentsStatus({
        path: { license_type_id: licenseTypeId },
      });
      if (response.error) {
        throw new Error("Failed to fetch template documents status");
      }
      return response.data;
    },
    enabled: isAuthSetupComplete && !!licenseTypeId,
  });

  const getTemplateStatus = (documentType: JurisdictionDocumentType) => {
    return templatesData?.templates?.find(
      (t) => t.template_type === documentType,
    );
  };

  const handleCreateTemplate = async (
    documentType: JurisdictionDocumentType,
    useDeletedTemplate?: boolean | null,
  ) => {
    if (!licenseTypeId) return;

    try {
      const requestBody = {
        use_deleted_template: useDeletedTemplate ?? null,
      };

      const response = await createTemplateDocumentEndpoint({
        path: {
          license_type_id: licenseTypeId,
          document_type: documentType,
        },
        body: requestBody,
      });

      if (response.error) {
        console.error("Template creation error:", response.error);
        toast.error("Failed to create template document");
        return;
      }

      // Check if we need user decision for deleted template
      if (response.data?.needs_user_decision) {
        setDeletedTemplateDialog({
          isOpen: true,
          documentType,
        });
        return;
      }

      // Open the edit URL in a new tab
      if (response.data?.edit_url) {
        window.open(response.data.edit_url, "_blank");
      }

      const message = response.data?.restored_from_deleted
        ? "Template restored and opened successfully"
        : "Template document created successfully";
      toast.success(message);

      // Refetch templates to update UI
      await refetchTemplates();
      queryClient.invalidateQueries({
        queryKey: ["template-documents-status", licenseTypeId],
      });
    } catch (error) {
      console.error("Error creating template:", error);
      toast.error("Failed to create template document");
    }
  };

  const handleDeletedTemplateDecision = async (useExisting: boolean) => {
    const documentType = deletedTemplateDialog.documentType;
    if (!documentType) return;

    // Close dialog
    setDeletedTemplateDialog({ isOpen: false, documentType: null });

    // Call create template again with the user's decision
    await handleCreateTemplate(documentType, useExisting);
  };

  const handleEditTemplate = (editUrl: string) => {
    window.open(editUrl, "_blank");
  };

  const handleDeleteTemplate = async (
    documentType: JurisdictionDocumentType,
  ) => {
    if (!licenseTypeId) return;

    try {
      const response = await deleteTemplateDocumentEndpoint({
        path: {
          license_type_id: licenseTypeId,
          document_type: documentType,
        },
      });

      if (response.error) {
        toast.error("Failed to delete template document");
        return;
      }

      toast.success("Template document deleted successfully");

      // Refetch templates to update UI
      await refetchTemplates();
      queryClient.invalidateQueries({
        queryKey: ["template-documents-status", licenseTypeId],
      });
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template document");
    }
  };

  if (!licenseTypeId) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Document Templates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Payment & Registration Templates */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-muted-foreground">
              Payment & Registration Templates
            </div>

            {/* Payment Receipt */}
            <div className="flex flex-col space-y-2">
              <div className="text-sm font-medium">Payment Receipt</div>
              {getTemplateStatus("payment_receipt")?.exists ? (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      const url =
                        getTemplateStatus("payment_receipt")?.edit_url;
                      if (url) handleEditTemplate(url);
                    }}
                    disabled={templatesLoading}
                  >
                    Edit Template
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleDeleteTemplate("payment_receipt")}
                    disabled={templatesLoading}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleCreateTemplate("payment_receipt")}
                  disabled={templatesLoading}
                >
                  Create Template
                </Button>
              )}
            </div>

            {/* Invoice (Google Doc template per license type) */}
            <div className="flex flex-col space-y-2">
              <div className="text-sm font-medium">Invoice</div>
              {getTemplateStatus("invoice")?.exists ? (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      const url = getTemplateStatus("invoice")?.edit_url;
                      if (url) handleEditTemplate(url);
                    }}
                    disabled={templatesLoading}
                  >
                    Edit Template
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleDeleteTemplate("invoice")}
                    disabled={templatesLoading}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleCreateTemplate("invoice")}
                  disabled={templatesLoading}
                >
                  Create Template
                </Button>
              )}
            </div>

            {/* Registration Certificate */}
            <div className="flex flex-col space-y-2">
              <div className="text-sm font-medium">
                Registration Certificate
              </div>
              {getTemplateStatus("registration_certificate")?.exists ? (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      const url = getTemplateStatus(
                        "registration_certificate",
                      )?.edit_url;
                      if (url) handleEditTemplate(url);
                    }}
                    disabled={templatesLoading}
                  >
                    Edit Template
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      handleDeleteTemplate("registration_certificate")
                    }
                    disabled={templatesLoading}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    handleCreateTemplate("registration_certificate")
                  }
                  disabled={templatesLoading}
                >
                  Create Template
                </Button>
              )}
            </div>
          </div>

          {/* Information Templates */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-muted-foreground">
              Information Templates
            </div>

            {/* Outreach Letter */}
            <div className="flex flex-col space-y-2">
              <div className="text-sm font-medium">Outreach Letter</div>
              {getTemplateStatus("outreach_letter")?.exists ? (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      const url =
                        getTemplateStatus("outreach_letter")?.edit_url;
                      if (url) handleEditTemplate(url);
                    }}
                    disabled={templatesLoading}
                  >
                    Edit Template
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleDeleteTemplate("outreach_letter")}
                    disabled={templatesLoading}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleCreateTemplate("outreach_letter")}
                  disabled={templatesLoading}
                >
                  Create Template
                </Button>
              )}
            </div>

            {/* Information Brochure */}
            <div className="flex flex-col space-y-2">
              <div className="text-sm font-medium">Information Brochure</div>
              {getTemplateStatus("information_brochure")?.exists ? (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      const url = getTemplateStatus(
                        "information_brochure",
                      )?.edit_url;
                      if (url) handleEditTemplate(url);
                    }}
                    disabled={templatesLoading}
                  >
                    Edit Template
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleDeleteTemplate("information_brochure")}
                    disabled={templatesLoading}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleCreateTemplate("information_brochure")}
                  disabled={templatesLoading}
                >
                  Create Template
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog for deleted template decision */}
      <Dialog
        open={deletedTemplateDialog.isOpen}
        onOpenChange={(open) =>
          setDeletedTemplateDialog({ isOpen: open, documentType: null })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Template Already Exists</DialogTitle>
            <DialogDescription>
              A previously deleted template was found for this document type.
              Would you like to restore and use the existing template, or create
              a brand new one?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => handleDeletedTemplateDecision(false)}
            >
              Create New Template
            </Button>
            <Button onClick={() => handleDeletedTemplateDecision(true)}>
              Use Existing Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
